/**
 * The browser's view of who is signed in.
 *
 * This is a **projection** of the verified server session, never the source of
 * it. Nothing here decides anything: the tenant comes from the token claim the
 * server verified, and the permissions and entitlements are the ones the server
 * resolved. A client that could edit this object could not thereby gain
 * anything, because every server decision is made again server-side — which is
 * the point of invariant 4 and of "UI hiding is not security".
 */
import type { RequestContext } from '@tessma/context';

export interface SessionUser {
  readonly id: string;
  readonly displayName: string;
  readonly initials: string;
  readonly roleLabel: string;
}

export interface Session {
  readonly authenticated: true;
  readonly user: SessionUser;
  readonly tenantId: string;
  readonly tenantName: string;
  readonly permissions: readonly string[];
  readonly entitlements: {
    readonly modules: readonly string[];
    readonly features: readonly string[];
    readonly version: number;
  };
  readonly permissionVersion: number;
  readonly scopeNodeIds: readonly string[];
  readonly stepUpSatisfied: boolean;
}

export interface AnonymousSession {
  readonly authenticated: false;
}

export type SessionState = Session | AnonymousSession;

export const ANONYMOUS: AnonymousSession = Object.freeze({ authenticated: false });

/**
 * The shape `authorise()` reads. Built here rather than with
 * `createRequestContext`, which is a server builder that takes verified claims
 * — a browser has no verified claims and must not appear to produce any.
 */
export function contextFrom(session: Session): RequestContext {
  return {
    tenantId: session.tenantId,
    actorId: session.user.id,
    actorType: 'tenant_user',
    onBehalfOfActorId: undefined,
    systemPurpose: undefined,
    sessionId: undefined,
    correlationId: '00000000-0000-0000-0000-000000000000',
    scopes: [],
    permissions: { values: new Set(session.permissions), version: session.permissionVersion },
    entitlements: {
      modules: new Set(session.entitlements.modules),
      features: new Set(session.entitlements.features),
      version: session.entitlements.version,
    },
    locale: 'en-GB',
    timeZone: 'Europe/London',
    requestedAt: new Date(),
  };
}

export interface SessionGateway {
  /** Reads the current session. Resolves to anonymous rather than throwing. */
  load: () => Promise<SessionState>;
  /** Starts the OIDC authorisation-code flow at the identity provider. */
  beginSignIn: (returnTo: string) => Promise<void>;
  /**
   * Ends the session here, and returns where to end it at the provider.
   *
   * There is deliberately no `completeSignIn`: the authorisation code is
   * exchanged SERVER-SIDE and never reaches JavaScript, so the browser has
   * nothing to complete. The provider redirects to the API, which sets the
   * session cookie and sends the browser on.
   */
  signOut: () => Promise<{ readonly endSessionUrl?: string }>;
}

/**
 * The HTTP gateway.
 *
 * The browser never holds a token: the session is a `HttpOnly`, `SameSite`
 * cookie the API sets, and every call carries it by virtue of being
 * same-origin. A token in JavaScript is a token in every XSS.
 */
export function createSessionGateway(baseUrl = '/api/v1'): SessionGateway {
  async function json<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${baseUrl}${path}`, { credentials: 'same-origin', ...init });
    if (!response.ok) throw new Error(`${String(response.status)} from ${path}`);
    return (await response.json()) as T;
  }

  return {
    async load() {
      try {
        return await json<SessionState>('/session');
      } catch {
        // A failure to read the session is not a session. Failing closed here
        // means an unauthenticated shell, never a partly-rendered one.
        return ANONYMOUS;
      }
    },
    async beginSignIn(returnTo) {
      const { authorisationUrl } = await json<{ authorisationUrl: string }>(
        `/auth/start?returnTo=${encodeURIComponent(returnTo)}`,
      );
      globalThis.location.assign(authorisationUrl);
    },
    async signOut() {
      try {
        return await json<{ endSessionUrl?: string }>('/auth/logout', {
          method: 'POST',
          credentials: 'same-origin',
        });
      } catch {
        // The local cookie is cleared by the response either way; a failure to
        // learn the provider's logout URL must not strand the user signed in.
        return {};
      }
    },
  };
}
